import "@/App.css";
import { useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { 
  Shield, 
  BadgeCheck, 
  Award, 
  TrendingUp, 
  Building2,
  ChevronRight,
  ChevronLeft,
  Check,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Heart,
  Users,
  DollarSign,
  FileCheck,
  Calendar,
  Globe,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ed1c798a-8c6c-4d0b-9772-eac6c30baea8/artifacts/qsb4sc2e_amplify-icon.svg";
const PLATFORM_GRAPHIC_URL = "https://customer-assets.emergentagent.com/job_ed1c798a-8c6c-4d0b-9772-eac6c30baea8/artifacts/r4fn7cor_Amplify_Platform_Graphic_HiRes.png";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Calendly popup function
const openCalendly = (e) => {
  e.preventDefault();
  if (window.Calendly) {
    window.Calendly.initPopupWidget({
      url: 'https://calendly.com/d/cxnm-px9-pj8/new-meeting'
    });
  }
};

// Testimonial Carousel Component
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const testimonials = [
    {
      quote: "I've worked with businesses in insurance and benefits for a decade. Amplify is one of the rare payroll and PEO companies that actually delivers on experience, service, and value. They stand up to the larger, more-marketed competition easily.",
      initials: "HB",
      title: "Industry Professional",
      subtitle: "10+ Years in Benefits & Insurance"
    },
    {
      quote: "Amplify HR is easy to use, site navigation is simple, and it makes it easy for employees and management. I use it every day for both functions, and I couldn't be happier with the system.",
      initials: "JB",
      title: "Daily User",
      subtitle: "Employee & Management Functions"
    },
    {
      quote: "What has impressed me the most is the long-term consistency of support from mostly the same key people over seven years. Answers from their team come quickly. And because we are still dealing with the same key members after 7 years, their solutions are reflective of my company's history with Amplify HR.",
      initials: "MW",
      title: "Long-Term Client",
      subtitle: "7+ Years with Amplify"
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentIndex];

  return (
    <div className="relative">
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 min-h-[280px] flex flex-col">
        <div className="absolute -top-3 -left-3 text-[#c99a2e] text-6xl font-serif leading-none">"</div>
        <p className="text-white/90 text-lg leading-relaxed mb-6 pl-6 flex-grow">
          {current.quote}
        </p>
        <div className="flex items-center justify-between pl-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3c68ff] to-[#4a6cf7] flex items-center justify-center text-white font-bold text-lg">
              {current.initials}
            </div>
            <div>
              <p className="text-white font-semibold">{current.title}</p>
              <p className="text-slate-400 text-sm">{current.subtitle}</p>
            </div>
          </div>
          {/* Navigation Arrows */}
          <div className="flex items-center gap-2">
            <button 
              onClick={prevTestimonial}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button 
              onClick={nextTestimonial}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Next testimonial"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        {/* Dots indicator */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-[#3c68ff]' : 'bg-white/30'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#00124a]/95 backdrop-blur-lg border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#hero" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Amplify" className="h-10 w-auto" />
            <span className="text-white font-bold text-xl tracking-tight">
              AMPLIFY
            </span>
          </a>
          <div className="hidden md:flex items-center gap-6">
            <a href="#what-is-peo" className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
              What is a PEO?
            </a>
            <a href="#platform" className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
              Platform
            </a>
            <a href="#prevantedge" className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
              PrevantEdge™
            </a>
            <button 
              onClick={openCalendly}
              data-testid="nav-schedule"
              className="btn-primary text-white rounded-md px-5 py-2 text-sm font-semibold"
            >
              Schedule Now
            </button>
            <a href="#contact" data-testid="nav-cta">
              <Button variant="outline" className="btn-secondary text-white rounded-md px-5 py-2 text-sm font-medium">
                Request Info
              </Button>
            </a>
            <a href="tel:8558088689" className="text-slate-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-1">
              <Phone className="w-4 h-4" />
              (855) 808-8689
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="hero-gradient min-h-[85vh] flex items-center pt-20 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#3c68ff]/10 via-transparent to-[#3c68ff]/5"></div>
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#3c68ff]/10 blur-3xl rounded-full"></div>
      {/* Gold accent bar at top */}
      <div className="absolute top-20 left-0 right-0 h-1 gold-accent-bar"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 relative z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left content - 7 columns */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-left lg:col-span-7"
          >
            <motion.div variants={fadeUp} className="mb-5">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#c99a2e]/10 border border-[#c99a2e]/30 text-[#c99a2e] text-[10px] font-bold uppercase tracking-[2px]">
                <BadgeCheck className="w-4 h-4" />
                IRS Certified PEO & ESAC Accredited
              </span>
            </motion.div>
            <motion.h1 
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-4"
            >
              <span className="block">Your Business.</span>
              <span className="block text-gradient">Amplified.</span>
            </motion.h1>
            <motion.p 
              variants={fadeUp}
              className="text-xl sm:text-2xl text-slate-200 font-medium mb-6"
            >
              Fortune 500 benefits. <span className="text-[#c99a2e]">Zero HR headaches.</span>
            </motion.p>
            <motion.p 
              variants={fadeUp}
              className="text-slate-400 text-base leading-relaxed mb-6 max-w-lg"
            >
              All backed by the <a href="#certification" className="text-[#3c68ff] underline hover:text-[#4a6cf7] transition-colors">highest certifications in the industry</a>.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center gap-4">
              <button 
                onClick={openCalendly}
                data-testid="hero-cta-primary"
                className="btn-primary text-white rounded-md px-6 py-3 text-sm font-bold flex items-center justify-center h-12"
              >
                Schedule Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <a href="#contact" data-testid="hero-cta-secondary">
                <Button variant="outline" className="btn-secondary text-white rounded-md px-6 py-3 text-sm font-medium h-12">
                  Request Info
                </Button>
              </a>
              <a href="tel:8558088689" className="text-slate-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-2 px-6 h-12 border border-white/20 rounded-md">
                <Phone className="w-4 h-4" />
                (855) 808-8689
              </a>
            </motion.div>
          </motion.div>
          
          {/* Right content - Testimonial Carousel */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex lg:col-span-5 flex-col justify-center"
            data-testid="hero-testimonial"
          >
            <TestimonialCarousel />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Credentials Bar - With Actual Logos
const CredentialsBar = () => {
  const imageCredentials = [
    { 
      image: "https://customer-assets.emergentagent.com/job_da3c9a75-84f9-4542-8d8c-8f7e7b6eecef/artifacts/x8u15il0_IRS%20CPEO.png",
      alt: "IRS Certified PEO"
    },
    { 
      image: "https://customer-assets.emergentagent.com/job_da3c9a75-84f9-4542-8d8c-8f7e7b6eecef/artifacts/dg9bzvow_ESAC-logo.png",
      alt: "ESAC Bonded Accredited"
    },
    { 
      image: "https://customer-assets.emergentagent.com/job_da3c9a75-84f9-4542-8d8c-8f7e7b6eecef/artifacts/j6smr12m_Forbes%202025.png",
      alt: "Forbes Advisor Best of 2025"
    },
    { 
      image: "https://customer-assets.emergentagent.com/job_da3c9a75-84f9-4542-8d8c-8f7e7b6eecef/artifacts/6y4z4ttx_G2%202025.png",
      alt: "G2 Users Love Us"
    },
    { 
      image: "https://customer-assets.emergentagent.com/job_da3c9a75-84f9-4542-8d8c-8f7e7b6eecef/artifacts/4j5dxnbj_Inc5000x4.jpg",
      alt: "Inc 5000 4-Time Winner"
    },
  ];

  // Google Reviews styled component
  const GoogleReviews = () => (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center gap-1 mb-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-[#4A5568] text-[#4A5568]" />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4A5568" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#4A5568" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#4A5568" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#4A5568" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span className="text-[#4A5568] font-semibold text-sm">Google</span>
      </div>
    </div>
  );

  return (
    <section className="bg-white border-b border-slate-100 py-6">
      <Marquee gradient={false} speed={30} pauseOnHover>
        <div className="flex items-center">
          {/* Google Reviews */}
          <div className="flex items-center justify-center credential-item h-20 w-44 mx-6" data-testid="credential-google">
            <GoogleReviews />
          </div>
          {imageCredentials.map((cred, index) => (
            <div 
              key={index}
              className="flex items-center justify-center credential-item h-20 w-44 mx-6"
              data-testid={`credential-${index}`}
            >
              <img 
                src={cred.image} 
                alt={cred.alt}
                className="h-16 w-auto object-contain max-w-[140px]"
              />
            </div>
          ))}
          {/* Duplicate for seamless scroll */}
          <div className="flex items-center justify-center credential-item h-20 w-44 mx-6">
            <GoogleReviews />
          </div>
          {imageCredentials.map((cred, index) => (
            <div 
              key={`dup-${index}`}
              className="flex items-center justify-center credential-item h-20 w-44 mx-6"
            >
              <img 
                src={cred.image} 
                alt={cred.alt}
                className="h-16 w-auto object-contain max-w-[140px]"
              />
            </div>
          ))}
        </div>
      </Marquee>
    </section>
  );
};

// What is a PEO Section
const WhatIsPeoSection = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "Fortune 500 Benefits",
      description: "Access premium health insurance, retirement plans, and perks typically reserved for large corporations."
    },
    {
      icon: FileCheck,
      title: "Full Compliance",
      description: "Stay protected with ACA, OSHA, EEOC, and multi-state tax compliance handled by certified experts."
    },
    {
      icon: Users,
      title: "HR Administration",
      description: "Offload payroll, onboarding, workers' comp, and employee relations to a dedicated team."
    },
    {
      icon: Shield,
      title: "Risk Mitigation",
      description: "Reduce liability with EPLI coverage, safety programs, and proactive HR support."
    }
  ];

  return (
    <section id="what-is-peo" className="py-12 md:py-16 bg-[#F1F5F9]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-left max-w-3xl mb-8"
        >
          <motion.span variants={fadeUp} className="text-[#c99a2e] font-bold text-[10px] uppercase tracking-[2px] mb-4 block">
            Understanding PEO
          </motion.span>
          <motion.h2 
            variants={fadeUp}
            className="text-3xl sm:text-4xl lg:text-[32px] font-extrabold text-[#00124a] mb-6 leading-[1.1]"
          >
            What is a Professional Employer Organization?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#4A5568] text-base sm:text-lg leading-[1.7]">
            A Professional Employer Organization is a firm that enters a co-employment relationship with a business, 
            taking on shared responsibility for payroll, HR administration, tax compliance, benefits, and workers' compensation 
            while the client retains full control over their operations and workforce.
          </motion.p>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          {benefits.map((benefit, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className="bg-white rounded-lg p-4 text-center border border-[#e2e8f0] hover:border-[#3c68ff]/30 hover:shadow-md transition-all"
              data-testid={`peo-benefit-${index}`}
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3c68ff] to-[#4a6cf7] flex items-center justify-center mx-auto mb-3">
                <benefit.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-[#00124a] mb-1">
                {benefit.title}
              </h3>
              <p className="text-[#6B7280] leading-snug text-xs sm:text-sm">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          id="certification"
          className="bg-[#00124a] rounded-lg p-8 md:p-12 scroll-mt-24"
        >
          <div className="flex items-start gap-4 mb-6">
            <Shield className="w-8 h-8 text-[#3c68ff] flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                Why Certification Matters
              </h3>
              <p className="text-slate-300 leading-[1.7] text-sm sm:text-base">
                Because a business is effectively entrusting its payroll taxes and workforce compliance to a third party, 
                it is critical to partner only with a PEO that holds both{" "}
                <span className="text-[#3c68ff] font-bold">ESAC accreditation</span> and{" "}
                <span className="text-[#3c68ff] font-bold">IRS Certified PEO status</span> — 
                the two highest independent credentialing standards in the industry — ensuring the financial safeguards, 
                regulatory accountability, and legal protections that no uncertified provider can offer.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 ml-0 sm:ml-12">
            <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#3c68ff]/10 border border-[#3c68ff]/30">
              <Check className="w-4 h-4 text-[#3c68ff]" />
              <span className="text-white text-sm sm:text-base font-semibold">IRS Certified</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#3c68ff]/10 border border-[#3c68ff]/30">
              <Check className="w-4 h-4 text-[#3c68ff]" />
              <span className="text-white text-sm sm:text-base font-semibold">ESAC Accredited</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#3c68ff]/10 border border-[#3c68ff]/30">
              <Check className="w-4 h-4 text-[#3c68ff]" />
              <span className="text-white text-sm sm:text-base font-semibold">Financial Safeguards</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Platform Section
const PlatformSection = () => {
  return (
    <section id="platform" className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto mb-8"
        >
          <motion.span variants={fadeUp} className="text-[#c99a2e] font-bold text-[10px] uppercase tracking-[2px] mb-4 block">
            All-In-One Platform
          </motion.span>
          <motion.h2 
            variants={fadeUp}
            className="text-3xl sm:text-4xl lg:text-[32px] font-extrabold text-[#00124a] mb-6 leading-[1.1]"
          >
            Everything You Need in One Place
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#4A5568] text-base leading-[1.7]">
            Our integrated platform delivers enterprise-grade HR, benefits, and compliance tools 
            built specifically for growing businesses.
          </motion.p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#3c68ff]/5 via-transparent to-[#3c68ff]/5 rounded-lg"></div>
          <div className="relative rounded-lg overflow-hidden platform-glow p-2 bg-gradient-to-r from-[#3c68ff]/20 to-[#4a6cf7]/20">
            <img 
              src={PLATFORM_GRAPHIC_URL} 
              alt="Amplify Core Platform" 
              className="w-full rounded-lg"
              data-testid="platform-graphic"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// PrevantEdge Section
const PrevantEdgeSection = () => {
  return (
    <section id="prevantedge" className="py-10 md:py-12 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto mb-8"
        >
          <motion.div variants={fadeUp} className="mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#DCFCE7] text-[#16A34A] text-[10px] font-bold uppercase tracking-[2px] mb-6">
              <Heart className="w-4 h-4" />
              Proprietary Benefits Program
            </div>
            <h2 
              className="text-3xl sm:text-4xl lg:text-[32px] font-extrabold text-[#00124a] mb-4 leading-[1.1]"
              data-testid="prevantedge-title"
            >
              PrevantEdge™
            </h2>
            <p className="text-[#6B7280] text-lg mb-6">
              <span className="italic">/ˈpre-vənt-ej/</span> <span className="ml-2">noun</span>
            </p>
          </motion.div>
          
          <motion.div variants={fadeUp} className="bg-white rounded-lg p-6 border border-[#e2e8f0] text-left">
            <p className="text-[#4A5568] text-base sm:text-lg leading-[1.7]">
              <span className="font-bold text-[#00124a]">1.</span> The competitive advantage an employer gains by offering 
              proactive, preventive employee benefits — programs designed to protect employees{" "}
              <span className="text-[#3c68ff] font-bold">before</span> illness, financial hardship, or burnout occurs.
            </p>
          </motion.div>
        </motion.div>

        {/* Real-World Example Comparison */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-10"
        >
          <h3 className="text-xl md:text-2xl font-bold text-[#00124a] text-center mb-8 leading-[1.1]">
            Example: $2,500 Inpatient Procedure
          </h3>
          
          <div className="grid md:grid-cols-2 gap-5 lg:gap-8">
            {/* Traditional Plan */}
            <div className="bg-white rounded-lg border-2 border-[#DC2626] overflow-hidden shadow-lg" data-testid="traditional-plan-card">
              <div className="bg-[#FEE2E2] px-5 py-3 border-b border-[#DC2626]/20">
                <div className="flex items-center gap-2">
                  <span className="text-[#DC2626] text-lg font-bold">✕</span>
                  <span className="text-[#00124a] font-bold text-sm uppercase tracking-wide">Traditional Plan</span>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#4A5568] text-sm sm:text-base">Procedure Cost</span>
                  <span className="font-semibold text-[#00124a] text-sm sm:text-base">$2,500</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#4A5568] text-sm sm:text-base">Copay</span>
                  <span className="font-semibold text-[#00124a] text-sm sm:text-base">$50</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#4A5568] text-sm sm:text-base">Insurance Pays</span>
                  <span className="font-semibold text-[#00124a] text-sm sm:text-base">$0</span>
                </div>
                <div className="flex justify-between items-center pt-3">
                  <span className="text-[#DC2626] font-bold text-sm sm:text-base">Employee Pays</span>
                  <span className="font-bold text-[#DC2626] text-xl">$2,550</span>
                </div>
                <p className="text-[#6B7280] text-xs sm:text-sm text-center italic pt-1">
                  Employee pays full amount (under $3,000 deductible)
                </p>
              </div>
            </div>

            {/* PrevantEdge Plan */}
            <div className="bg-white rounded-lg border-2 border-[#16A34A] overflow-hidden shadow-lg" data-testid="prevantedge-plan-card">
              <div className="bg-[#DCFCE7] px-5 py-3 border-b border-[#16A34A]/20">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#16A34A]" />
                  <span className="text-[#00124a] font-bold text-sm uppercase tracking-wide">PrevantEdge™</span>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#4A5568] text-sm sm:text-base">Procedure Cost</span>
                  <span className="font-semibold text-[#00124a] text-sm sm:text-base">$2,500</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#4A5568] text-sm sm:text-base">Copay</span>
                  <span className="font-semibold text-[#00124a] text-sm sm:text-base">$50</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#16A34A] font-semibold text-sm sm:text-base">GAP Benefit</span>
                  <span className="font-semibold text-[#16A34A] text-sm sm:text-base">$2,500</span>
                </div>
                <div className="flex justify-between items-center pt-3">
                  <span className="text-[#00124a] font-bold text-sm sm:text-base">Estimated Employee Cost</span>
                  <span className="font-bold text-[#16A34A] text-xl">$50</span>
                </div>
                <p className="text-[#6B7280] text-xs sm:text-sm text-center italic pt-1">
                  PrevantEdge™ program is designed to cover eligible expenses at point of service. Employee pays the copay.*
                </p>
              </div>
            </div>
          </div>

          <p className="text-[11px] sm:text-xs text-[#9CA3AF] text-center mt-6 max-w-4xl mx-auto leading-relaxed">
            PrevantEdge™ is a proprietary benefits strategy offered exclusively through Amplify HR Management's Professional Employer Organization (PEO) relationship and is not a standalone insurance product. All scenarios, visuals, and descriptions on this page are illustrative and presented for informational purposes only. Benefits, coverage levels, GAP coverage amounts, and employer savings vary by group and are subject to plan terms, conditions, and underwriting.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Progressive CTA Form
const CTAForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    employeeCount: '',
    currentProvider: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step < 2) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      const data = await response.json();
      console.log('Form submitted successfully:', data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = formData.firstName && formData.lastName && formData.email;
  const isStep2Valid = formData.company && formData.employeeCount;

  if (submitted) {
    return (
      <section id="contact" className="py-12 md:py-16 hero-gradient">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0a2472] rounded-lg p-12 border border-[#3c68ff]/30"
          >
            <div className="w-20 h-20 rounded-full bg-[#DCFCE7] flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#16A34A]" />
            </div>
            <h3 className="text-2xl font-extrabold text-white mb-4">
              Thank You, {formData.firstName}!
            </h3>
            <p className="text-slate-300 text-base leading-[1.7] mb-6">
              We've received your request. Our team will reach out within 24 hours 
              to discuss how Amplify can help {formData.company}.
            </p>
            <p className="text-slate-300 text-base leading-[1.7] mb-6">
              Want to get started sooner? Schedule a call now:
            </p>
            <button 
              onClick={openCalendly}
              className="inline-flex items-center gap-2"
              data-testid="schedule-now-success"
            >
              <span className="btn-primary text-white rounded-md px-8 py-4 font-bold flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Now
              </span>
            </button>
            <p className="text-slate-400 text-sm mt-4">
              Or call us at <a href="tel:8558088689" className="text-[#3c68ff] hover:underline">(855) 808-8689</a>
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-12 md:py-16 hero-gradient">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-left"
          >
            <motion.span variants={fadeUp} className="text-[#c99a2e] font-bold text-[10px] uppercase tracking-[2px] mb-4 block">
              Get Started Today
            </motion.span>
            <motion.h2 
              variants={fadeUp}
              className="text-3xl sm:text-4xl lg:text-[32px] font-extrabold text-white mb-6 leading-[1.1]"
            >
              Ready to Amplify Your Business?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-300 text-base leading-[1.7] mb-8">
              Join thousands of professionals that trust Amplify for their HR, benefits, and compliance needs. 
              Get a quote in days, not weeks.
            </motion.p>
            <motion.div variants={fadeUp} className="space-y-4">
              <button 
                onClick={openCalendly}
                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors group"
                data-testid="schedule-now-link"
              >
                <div className="w-10 h-10 rounded-lg bg-[#c99a2e]/20 flex items-center justify-center group-hover:bg-[#c99a2e]/30 transition-colors">
                  <Calendar className="w-5 h-5 text-[#c99a2e]" />
                </div>
                <span className="font-semibold">Schedule Now</span>
              </button>
              <a href="tel:8558088689" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#3c68ff]/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#3c68ff]" />
                </div>
                <span>(855) 808-8689</span>
              </a>
              <a href="mailto:success@amplifyhrm.com" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#3c68ff]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#3c68ff]" />
                </div>
                <span>success@amplifyhrm.com</span>
              </a>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-[#3c68ff]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#3c68ff]" />
                </div>
                <span>Nationwide Coverage</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-[#0a2472] rounded-lg p-8 border border-[#3c68ff]/30">
              {/* Step Indicators */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${step >= 1 ? 'bg-[#3c68ff] text-white' : 'bg-[#00124a] text-slate-400'}`}>
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">
                    {step > 1 ? <Check className="w-3 h-3" /> : '1'}
                  </span>
                  Contact Info
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500" />
                <div className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${step >= 2 ? 'bg-[#3c68ff] text-white' : 'bg-[#00124a] text-slate-400'}`}>
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                  Company Details
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {step === 1 && (
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">First Name</label>
                        <Input 
                          type="text"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="form-input rounded-md bg-[#00124a] border-[#3c68ff]/30 !text-white placeholder:text-slate-500 caret-white"
                          data-testid="input-first-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">Last Name</label>
                        <Input 
                          type="text"
                          placeholder="Smith"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="form-input rounded-md bg-[#00124a] border-[#3c68ff]/30 !text-white placeholder:text-slate-500 caret-white"
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Work Email</label>
                      <Input 
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="form-input rounded-md bg-[#00124a] border-[#3c68ff]/30 !text-white placeholder:text-slate-500 caret-white"
                        data-testid="input-email"
                      />
                    </div>
                    <Button 
                      type="button"
                      onClick={handleNext}
                      disabled={!isStep1Valid}
                      className="w-full btn-primary text-white rounded-md py-6 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="btn-next-step"
                    >
                      Get My Free Quote
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Company Name</label>
                      <Input 
                        type="text"
                        placeholder="Acme Inc."
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="form-input rounded-md bg-[#00124a] border-[#3c68ff]/30 !text-white placeholder:text-slate-500 caret-white"
                        data-testid="input-company"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Number of Employees</label>
                      <Select 
                        value={formData.employeeCount}
                        onValueChange={(value) => handleInputChange('employeeCount', value)}
                      >
                        <SelectTrigger className="form-input rounded-md bg-[#00124a] border-[#3c68ff]/30 text-white" data-testid="select-employee-count">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#00124a] border-[#3c68ff]/30 text-white">
                          <SelectItem value="1-10" className="text-white hover:bg-[#3c68ff]/20 focus:bg-[#3c68ff]/20 focus:text-white">1-10 employees</SelectItem>
                          <SelectItem value="11-50" className="text-white hover:bg-[#3c68ff]/20 focus:bg-[#3c68ff]/20 focus:text-white">11-50 employees</SelectItem>
                          <SelectItem value="51-100" className="text-white hover:bg-[#3c68ff]/20 focus:bg-[#3c68ff]/20 focus:text-white">51-100 employees</SelectItem>
                          <SelectItem value="101-250" className="text-white hover:bg-[#3c68ff]/20 focus:bg-[#3c68ff]/20 focus:text-white">101-250 employees</SelectItem>
                          <SelectItem value="251-500" className="text-white hover:bg-[#3c68ff]/20 focus:bg-[#3c68ff]/20 focus:text-white">251-500 employees</SelectItem>
                          <SelectItem value="500+" className="text-white hover:bg-[#3c68ff]/20 focus:bg-[#3c68ff]/20 focus:text-white">500+ employees</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Current Provider (Optional)</label>
                      <Input 
                        type="text"
                        placeholder="ADP, Paychex, etc."
                        value={formData.currentProvider}
                        onChange={(e) => handleInputChange('currentProvider', e.target.value)}
                        className="form-input rounded-md bg-[#00124a] border-[#3c68ff]/30 !text-white placeholder:text-slate-500 caret-white"
                        data-testid="input-current-provider"
                      />
                    </div>
                    <div className="flex gap-4">
                      <Button 
                        type="button"
                        onClick={handleBack}
                        variant="outline"
                        className="flex-1 btn-secondary text-white rounded-md py-6 font-medium"
                        data-testid="btn-back"
                      >
                        Back
                      </Button>
                      <Button 
                        type="submit"
                        disabled={!isStep2Valid || isSubmitting}
                        className="flex-1 btn-primary text-white rounded-md py-6 font-bold disabled:opacity-50"
                        data-testid="btn-submit"
                      >
                        {isSubmitting ? 'Submitting...' : 'Get My Quote'}
                      </Button>
                    </div>
                    {submitError && (
                      <p className="text-red-400 text-sm text-center mt-4">{submitError}</p>
                    )}
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#00124a] border-t border-[#3c68ff]/20 py-6">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top Row - Logo, Certifications, Contact */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6 pb-6 border-b border-[#3c68ff]/20">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Amplify" className="h-8 w-auto" />
            <span className="text-white font-bold text-lg">AMPLIFY</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider">
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#3c68ff]" /> IRS Certified</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#3c68ff]" /> ESAC</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#3c68ff]" /> Inc 5000</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#3c68ff]" /> Forbes</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#3c68ff]" /> G2</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <a href="tel:8558088689" className="hover:text-white transition-colors flex items-center gap-1">
              <Phone className="w-4 h-4" /> (855) 808-8689
            </a>
            <span className="text-[#3c68ff]">|</span>
            <a href="mailto:success@amplifyhrm.com" className="hover:text-white transition-colors">success@amplifyhrm.com</a>
            <span className="text-[#3c68ff]">|</span>
            <a href="https://amplifyhrm.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">amplifyhrm.com</a>
          </div>
        </div>
        
        {/* Copyright & Legal Links */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] text-[#6B7280]">
          <p>© {new Date().getFullYear()} Amplify HR Management, LLC. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link 
              to="/privacy" 
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="hover:text-white transition-colors"
            >
              Terms of Service
            </Link>
          </div>
          <p>Amplify HR Management is a professional employer organization (PEO). Services may vary by state.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Navigation />
      <HeroSection />
      <CredentialsBar />
      <WhatIsPeoSection />
      <PrevantEdgeSection />
      <PlatformSection />
      <CTAForm />
      <Footer />
    </div>
  );
}

export default App;
